import { logger } from "@/utils/logger";
/**
 * 路由守卫配置
 * 处理权限验证、登录检查等
 */
import type { Router } from "vue-router";
import { useUserStore } from "@/stores/user";
import { checkAndRestoreAuth } from "@/utils/authCheck";
import { ElMessage } from "element-plus";

/**
 * 无需登录的页面路由
 */
const PUBLIC_ROUTES = ["/login", "/404", "/register", "/forgot-password"];

/**
 * 管理员专用路由
 */
const ADMIN_ROUTES = ["/home/permission"];

/**
 * 设置路由守卫
 * @param router Vue Router实例
 */
export function setupRouterGuard(router: Router) {
  // 全局前置守卫
  router.beforeEach(async (to, from, next) => {
    logger.log(`路由跳转: ${from.path} -> ${to.path}`);

    // 设置页面标题
    if (to.meta?.title) {
      document.title = `${to.meta.title} - Cookpit管理后台`;
    } else {
      document.title = "Cookpit管理后台";
    }

    // 检查是否为公开路由
    if (PUBLIC_ROUTES.includes(to.path) || to.meta?.requireAuth === false) {
      next();
      return;
    }

    const userStore = useUserStore();

    // 尝试恢复认证状态
    if (!userStore.isAuthenticated) {
      try {
        const restored = await checkAndRestoreAuth();
        if (restored) {
          await userStore.initFromStorage();
        }
      } catch (error) {
        logger.error("恢复认证状态失败:", error);
      }
    }

    // 检查是否已登录
    if (!userStore.isAuthenticated) {
      logger.warn("用户未登录，跳转到登录页");
      next({
        path: "/login",
        query: { redirect: to.fullPath },
      });
      return;
    }

    // 受角色/权限门控的路由: 冷恢复时完整用户信息(含 roles)由 checkAndRestoreAuth
    // 在后台异步加载, 必须等它就绪再判定 —— 否则 roles 还是空数组, 有权限的用户
    // 通过书签/刷新/直接输 URL 进入会被误拒(SPA 跳转因信息已加载而正常, 表现为时好时坏)
    if (ADMIN_ROUTES.includes(to.path) || to.meta?.roles || to.meta?.permissions) {
      await userStore.waitForUserInfo();
    }

    // 检查管理员权限
    if (ADMIN_ROUTES.includes(to.path)) {
      if (!userStore.hasRole("admin")) {
        ElMessage.error("权限不足，无法访问该页面");
        next(from.path === "/" ? "/home" : false);
        return;
      }
    }

    // 检查页面权限
    if (to.meta?.permissions) {
      const hasPermission =
        userStore.hasRole("admin") ||
        checkRoutePermission(
          to.meta.permissions as string[],
          userStore.currentUser.permissions || [],
        );
      if (!hasPermission) {
        ElMessage.error("权限不足，无法访问该页面");
        next(from.path === "/" ? "/home" : false);
        return;
      }
    }

    // 检查角色权限
    if (to.meta?.roles) {
      const hasRole = checkRouteRole(to.meta.roles as string[], userStore.currentUser.roles || []);
      if (!hasRole) {
        ElMessage.error("角色权限不足，无法访问该页面");
        next(from.path === "/" ? "/home" : false);
        return;
      }
    }

    next();
  });

  // 全局后置守卫
  router.afterEach((to, from) => {
    logger.log(`路由跳转完成: ${from.path} -> ${to.path}`);

    // 滚动到顶部
    if (to.path !== from.path) {
      window.scrollTo(0, 0);
    }

    // 关闭loading状态
    const loadingElement = document.querySelector(".route-loading");
    if (loadingElement) {
      loadingElement.remove();
    }
  });

  // 路由错误处理
  router.onError((error) => {
    logger.error("路由错误:", error);
    ElMessage.error("页面加载失败，请刷新重试");
  });
}

/**
 * 检查路由权限
 * @param requiredPermissions 所需权限列表
 * @param userPermissions 用户权限列表
 * @returns 是否有权限
 */
function checkRoutePermission(requiredPermissions: string[], userPermissions: string[]): boolean {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  return requiredPermissions.some((permission) =>
    userPermissions.some((userPerm) => userPerm.toLowerCase().includes(permission.toLowerCase())),
  );
}

/**
 * 检查路由角色
 * @param requiredRoles 所需角色列表
 * @param userRoles 用户角色列表
 * @returns 是否有角色权限
 */
function checkRouteRole(requiredRoles: string[], userRoles: string[]): boolean {
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }

  return requiredRoles.some((role) => userRoles.includes(role));
}

/**
 * 动态添加路由
 * @param router Vue Router实例
 * @param userPermissions 用户权限
 */
export function addDynamicRoutes(router: Router, userPermissions: string[]) {
  // 根据用户权限动态添加路由
  const dynamicRoutes = generateDynamicRoutes(userPermissions);

  dynamicRoutes.forEach((route) => {
    router.addRoute("MainLayout", route);
  });
}

/**
 * 根据权限生成动态路由
 * @param permissions 用户权限
 * @returns 动态路由配置
 */
function generateDynamicRoutes(permissions: string[]) {
  const routes: any[] = [];

  // 根据权限添加相应的路由
  if (permissions.some((p) => p.includes("admin"))) {
    routes.push({
      path: "/admin",
      name: "Admin",
      component: () => import("@/views/system/ApiTest.vue"),
      meta: {
        title: "系统管理",
        roles: ["admin"],
      },
    });
  }

  // 可以根据需要添加更多动态路由

  return routes;
}

/**
 * 重置路由
 * @param router Vue Router实例
 */
export function resetRouter(router: Router) {
  // 清除动态添加的路由
  const newRouter = router;
  (router as any).matcher = (newRouter as any).matcher;
}
